import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { User, Phone, Mail, MapPin, Wrench, Download, Smartphone } from "lucide-react";

export default function EngineerProfile() {
  const { user } = useAuth();
  const [eng, setEng] = useState(null);
  const [installable, setInstallable] = useState(!!window.__deferredPrompt);

  useEffect(() => {
    if (user?.engineer_id) {
      api.get("/engineers").then((r) => {
        setEng(r.data.find((e) => e.id === user.engineer_id) || null);
      });
    }
    const onInstallable = () => setInstallable(true);
    window.addEventListener("pwa-installable", onInstallable);
    return () => window.removeEventListener("pwa-installable", onInstallable);
  }, [user]);

  const install = async () => {
    const prompt = window.__deferredPrompt;
    if (!prompt) {
      toast.info("On iPhone: tap Share → 'Add to Home Screen'. On Android: use the browser menu → 'Install app'.");
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") toast.success("App installed to your home screen.");
    window.__deferredPrompt = null;
    setInstallable(false);
  };

  return (
    <div className="p-5 fade-up">
      <h1 className="font-heading text-2xl font-black tracking-tight mb-5">Profile</h1>

      <div className="border border-border bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-14 w-14 bg-klein/10 text-klein flex items-center justify-center">
            <User size={26} />
          </div>
          <div>
            <div className="font-heading font-bold text-lg" data-testid="profile-name">{user?.name}</div>
            <div className="label-caps text-muted-foreground">{user?.role}</div>
          </div>
        </div>

        <div className="space-y-2 text-sm border-t border-border pt-4">
          <div className="flex items-center gap-2"><Mail size={15} className="text-muted-foreground" /> {user?.email}</div>
          {eng && (
            <>
              <div className="flex items-center gap-2"><Phone size={15} className="text-muted-foreground" /> {eng.mobile}</div>
              <div className="flex items-center gap-2"><MapPin size={15} className="text-muted-foreground" /> Zone: {eng.zone || "—"}</div>
              <div className="flex items-start gap-2">
                <Wrench size={15} className="text-muted-foreground mt-1" />
                <div className="flex flex-wrap gap-1">
                  {eng.skill_tags?.length
                    ? eng.skill_tags.map((s) => <span key={s} className="text-[10px] bg-secondary px-1.5 py-0.5">{s}</span>)
                    : <span className="text-muted-foreground">No skills set</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Install PWA */}
      <div className="border border-klein bg-klein/5 p-5 mt-5">
        <div className="flex items-center gap-2 font-heading font-bold mb-1">
          <Smartphone size={18} className="text-klein" /> Install the field app
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Add RoSo Field to your home screen for one-tap access and a full-screen, app-like experience.
        </p>
        <Button onClick={install} data-testid="install-pwa-button" className="rounded-sm bg-klein hover:bg-klein/90 text-white w-full h-11">
          <Download size={16} className="mr-1" /> {installable ? "Install app" : "How to install"}
        </Button>
      </div>
    </div>
  );
}
